import { useState, useRef, useEffect, ReactNode } from 'react';
// DISABLED: framer-motion causes React 19 Concurrent Mode crash loop
const motion = { div: 'div' };
const useMotionValue = (v: any) => v;
const useTransform = (v: any, ...args: any[]) => v;
import { RefreshIcon } from './Icons';

interface SwipeableRowProps {
  children: ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  leftAction?: { label: string; icon: ReactNode; color: string };
  rightAction?: { label: string; icon: ReactNode; color: string };
  threshold?: number;
}

export function SwipeableRow({ 
  children, 
  onSwipeLeft, 
  onSwipeRight,
  leftAction,
  rightAction,
  threshold = 0.3
}: SwipeableRowProps) {
  const x = useMotionValue(0);
  const rowRef = useRef<HTMLDivElement>(null);
  
  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const rowWidth = rowRef.current?.offsetWidth || 300;
    const swipeThreshold = rowWidth * threshold;
    
    if (info.offset.x < -swipeThreshold && onSwipeLeft) {
      onSwipeLeft();
    } else if (info.offset.x > swipeThreshold && onSwipeRight) {
      onSwipeRight();
    }
  };

  return (
    <div ref={rowRef} className="relative overflow-hidden">
      {/* Left action background */}
      {leftAction && (
        <motion.div 
          className="absolute inset-0 flex items-center justify-end pr-4 bg-red-50"
        >
          <div className={`${leftAction.color} text-white px-4 py-2 rounded-lg flex items-center gap-2`}>
            {leftAction.icon}
            <span>{leftAction.label}</span>
          </div>
        </motion.div>
      )}
      
      {/* Right action background */}
      {rightAction && (
        <motion.div 
          className="absolute inset-0 flex items-center justify-start pl-4 bg-green-50"
        >
          <div className={`${rightAction.color} text-white px-4 py-2 rounded-lg flex items-center gap-2`}>
            {rightAction.icon}
            <span>{rightAction.label}</span>
          </div>
        </motion.div>
      )}
      
      {/* Draggable content */}
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.3}
        onDragEnd={handleDragEnd}
        style={{ x }}
        className="bg-white touch-manipulation"
      >
        {children}
      </motion.div>
    </div>
  );
}

// Pull-to-refresh component
interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  threshold?: number;
}

export function PullToRefresh({ onRefresh, children, threshold = 80 }: PullToRefreshProps) {
  const y = useMotionValue(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  
  const rotate = useTransform(y, [0, threshold], [0, 180]);
  
  const handleDragEnd = async (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.y > threshold && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Pull indicator */}
      <motion.div
        className="absolute top-0 left-0 right-0 flex items-center justify-center h-16 pointer-events-none"
        style={{ y: isPulling ? y : 0, rotate, opacity: isPulling ? 1 : 0 }}
      >
        {isRefreshing ? (
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        ) : (
          <RefreshIcon className="w-6 h-6 text-gray-400" />
        )}
      </motion.div>
      
      {/* Draggable content */}
      <motion.div
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0.5}
        onDragStart={() => setIsPulling(true)}
        onDragEnd={(e, info) => {
          setIsPulling(false);
          handleDragEnd(e, info);
        }}
        className="min-h-screen"
      >
        {children}
      </motion.div>
    </div>
  );
}

// Horizontal swipe between pages
interface SwipeNavigatorProps {
  children: ReactNode[];
  activeIndex: number;
  onIndexChange: (index: number) => void;
}

export function SwipeNavigator({ children, activeIndex, onIndexChange }: SwipeNavigatorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 50;
    if (info.offset.x < -threshold && activeIndex < children.length - 1) {
      onIndexChange(activeIndex + 1);
    } else if (info.offset.x > threshold && activeIndex > 0) {
      onIndexChange(activeIndex - 1);
    }
  };

  return (
    <div ref={containerRef} className="overflow-hidden">
      <motion.div
        className="flex"
        drag="x"
        dragConstraints={{ left: -(activeIndex * (containerRef.current?.offsetWidth || 300)), right: -(activeIndex * (containerRef.current?.offsetWidth || 300)) }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        animate={{ x: -(activeIndex * (containerRef.current?.offsetWidth || 300)) }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {children.map((child, i) => (
          <div key={i} className="min-w-full">
            {child}
          </div>
        ))}
      </motion.div>
      
      {/* Page indicators */}
      <div className="flex justify-center gap-2 py-2">
        {children.map((_, i) => (
          <button
            key={i}
            onClick={() => onIndexChange(i)}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === activeIndex ? 'bg-blue-500' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default { SwipeableRow, PullToRefresh, SwipeNavigator };
